/**
 * Buildings - Physical structures at FBO locations
 * Includes hangars, ramps, tiedown areas, and other outdoor spaces
 */

import type { AreaPlacementFields, AreaPlacementFieldsInput } from './area-placement'

/** Area structural type classification (matches database area_type enum) */
export type AreaType =
  | 'single_unit_hangar'       // Standalone enclosed hangar, one open space
  | 'box_hangar_single_row'    // Row of individual box units, single-sided
  | 'box_hangar_double_row'    // Two rows of box units, back-to-back
  | 't_hangar_row'             // Row of T-shaped individual units
  | 'nested_t_hangar_row'      // Interlocking T-hangar units
  | 'arch_hangar'              // Semi-cylindrical arch/Quonset hangar
  // Outdoor areas
  | 'tiedown_area'             // Open-air tiedown parking
  | 'ramp_area'                // Paved apron/ramp parking
  | 'shade_structure'          // Covered without full walls
  | 'exit_point'               // Runway/taxiway exit zone (non-allocatable)
  | 'fuel_depot'               // Fuel farm / truck dispatch point (non-allocatable)
  // Non-aircraft buildings
  | 'terminal'                 // Terminal/FBO building
  | 'office'                   // Office/admin building
  // Custom
  | 'custom'                   // User-defined structure

/** Backward-compatible alias */
export type HangarType = AreaType

/** Area type display labels */
export const AREA_TYPE_LABELS: Record<AreaType, string> = {
  single_unit_hangar: 'Single Unit Hangar',
  box_hangar_single_row: 'Box Hangar (Single Row)',
  box_hangar_double_row: 'Box Hangar (Double Row)',
  t_hangar_row: 'T-Hangar Row',
  nested_t_hangar_row: 'Nested T-Hangar Row',
  arch_hangar: 'Arch Hangar',
  tiedown_area: 'Tiedown Area',
  ramp_area: 'Ramp Area',
  shade_structure: 'Shade Structure',
  exit_point: 'Exit (Runway Exit)',
  fuel_depot: 'Fuel Depot',
  terminal: 'Terminal',
  office: 'Office',
  custom: 'Custom',
}

/** Backward-compatible alias */
export const HANGAR_TYPE_LABELS = AREA_TYPE_LABELS

/** All area types for select dropdowns (grouped logically) */
export const AREA_TYPES: AreaType[] = [
  // Enclosed hangars
  'single_unit_hangar',
  'box_hangar_single_row',
  'box_hangar_double_row',
  // T-hangars
  't_hangar_row',
  'nested_t_hangar_row',
  // Arch hangars
  'arch_hangar',
  // Outdoor areas
  'shade_structure',
  'ramp_area',
  'tiedown_area',
  // Event placement
  'exit_point',
  'fuel_depot',
  // Non-aircraft buildings
  'terminal',
  'office',
  // Custom
  'custom',
]

/** Backward-compatible alias */
export const HANGAR_TYPES = AREA_TYPES

/** T-hangar structural variants (require unit configuration) */
export const T_HANGAR_AREA_TYPES: AreaType[] = ['t_hangar_row', 'nested_t_hangar_row']

/** Multi-unit row types (contain multiple individual units) */
export const MULTI_UNIT_AREA_TYPES: AreaType[] = [
  'box_hangar_single_row',
  'box_hangar_double_row',
  't_hangar_row',
  'nested_t_hangar_row',
]

/** Outdoor area types (no doors, different dimension requirements) */
export const OUTDOOR_AREA_TYPES: AreaType[] = ['ramp_area', 'tiedown_area', 'shade_structure']

/** Backward-compatible aliases */
export const T_HANGAR_TYPES = T_HANGAR_AREA_TYPES
export const OUTDOOR_BUILDING_TYPES = OUTDOOR_AREA_TYPES

/** Check if area type is T-hangar variant */
export const isTHangarAreaType = (type: AreaType): boolean =>
  T_HANGAR_AREA_TYPES.includes(type)

/** Check if area type is multi-unit row */
export const isMultiUnitAreaType = (type: AreaType): boolean =>
  MULTI_UNIT_AREA_TYPES.includes(type)

/** Check if area type is outdoor (no doors required) */
export const isOutdoorAreaType = (type: AreaType): boolean =>
  OUTDOOR_AREA_TYPES.includes(type)

/** Check if area type is an Exit area ("Runway Exit", non-allocatable) */
export const isExitAreaType = (type: AreaType): boolean => type === 'exit_point'

/** Check if area type is a Fuel Depot (fuel farm / truck dispatch, non-allocatable) */
export const isFuelDepotType = (type: AreaType): boolean => type === 'fuel_depot'

/** Backward-compatible aliases */
export const isTHangarType = isTHangarAreaType
export const isOutdoorType = isOutdoorAreaType

/** Hangar status */
export type HangarStatus = 'active' | 'maintenance' | 'construction' | 'inactive'

/** Hangar status display labels */
export const HANGAR_STATUS_LABELS: Record<HangarStatus, string> = {
  active: 'Active',
  maintenance: 'Maintenance',
  construction: 'Construction',
  inactive: 'Inactive',
}

/** All hangar statuses */
export const HANGAR_STATUSES: HangarStatus[] = [
  'active',
  'maintenance',
  'construction',
  'inactive',
]

/** Area code prefixes by structural type - used for auto-generating codes */
export const AREA_TYPE_PREFIXES: Record<AreaType, string> = {
  single_unit_hangar: 'HGR',
  box_hangar_single_row: 'BXS',
  box_hangar_double_row: 'BXD',
  t_hangar_row: 'THR',
  nested_t_hangar_row: 'NTR',
  arch_hangar: 'ARC',
  tiedown_area: 'TDN',
  ramp_area: 'RMP',
  shade_structure: 'SHD',
  exit_point: 'EXT',
  fuel_depot: 'FUL',
  terminal: 'TRM',
  office: 'OFC',
  custom: 'CST',
}

/** Pricing model for hangar storage */
export type PricingModel = 'fixed' | 'per_sqft'

/** Pricing model display labels */
export const PRICING_MODEL_LABELS: Record<PricingModel, string> = {
  fixed: 'Fixed Price',
  per_sqft: 'Price per Sq Ft',
}

/** All pricing models */
export const PRICING_MODELS: PricingModel[] = ['fixed', 'per_sqft']

/** T-hangar unit template configuration */
export interface TRowUnitTemplate {
  door_width: number
  depth: number
  tail_width: number
}

/** T-row hangar type configuration */
export interface TRowTypeConfig {
  unit_count: number
  nesting_style: 'standard' | 'offset'
  /** Layout: single row or nested (teeth pattern with two rows) */
  layout?: 'single' | 'nested'
  unit_template: TRowUnitTemplate
  shared_taxilane_width: number
}

/** Community hangar door position */
export interface DoorPosition {
  side: 'north' | 'south' | 'east' | 'west'
  width: number
  offset: number
}

/** Community hangar type configuration */
export interface CommunityTypeConfig {
  door_positions: DoorPosition[]
  default_zones: string[]
  max_aircraft: number
}

/** Type config is a union of possible configs */
export type HangarTypeConfig = TRowTypeConfig | CommunityTypeConfig | Record<string, unknown>

/** Database row type for areas table */
export interface HangarRow extends AreaPlacementFields {
  id: string
  organization_id: string
  location_id: string
  name: string
  area_code: string
  type: HangarType
  exterior_width: number
  exterior_depth: number
  exterior_height: number
  total_footprint_sqft: number // Generated column
  latitude: number
  longitude: number
  altitude: number | null
  rotation: number | null
  door_width: number | null
  door_height: number | null
  // Building features
  construction_year: number | null
  fire_suppression: boolean | null
  security_system: boolean | null
  // Pricing
  monthly_rate_private: number | null
  monthly_rate_shared: number | null
  transient_rate_nightly: number | null
  pricing_model: PricingModel
  rate_per_sqft_private: number | null
  rate_per_sqft_shared: number | null
  status: HangarStatus
  type_config: HangarTypeConfig
  settings: Record<string, unknown>
  // Event auto-placement authoring (area level) is inherited from AreaPlacementFields.
  created_at: string
  created_by: string | null
  updated_at: string | null
  updated_by: string | null
  deleted_at: string | null
  deleted_by: string | null
}

/** Insert type for creating new areas */
export interface HangarInsert extends AreaPlacementFieldsInput {
  organization_id: string
  location_id: string
  name: string
  area_code: string
  type: HangarType
  exterior_width: number
  exterior_depth: number
  exterior_height: number
  latitude: number
  longitude: number
  altitude?: number | null
  rotation?: number | null
  door_width?: number | null
  door_height?: number | null
  // Building features
  construction_year?: number | null
  fire_suppression?: boolean | null
  security_system?: boolean | null
  // Pricing
  monthly_rate_private?: number | null
  monthly_rate_shared?: number | null
  transient_rate_nightly?: number | null
  pricing_model?: PricingModel
  rate_per_sqft_private?: number | null
  rate_per_sqft_shared?: number | null
  status?: HangarStatus
  type_config?: HangarTypeConfig
  settings?: Record<string, unknown>
}

/** Update type for modifying areas */
export interface HangarUpdate extends AreaPlacementFieldsInput {
  name?: string
  area_code?: string
  type?: HangarType
  exterior_width?: number
  exterior_depth?: number
  exterior_height?: number
  latitude?: number
  longitude?: number
  altitude?: number | null
  rotation?: number | null
  door_width?: number | null
  door_height?: number | null
  // Building features
  construction_year?: number | null
  fire_suppression?: boolean | null
  security_system?: boolean | null
  // Pricing
  monthly_rate_private?: number | null
  monthly_rate_shared?: number | null
  transient_rate_nightly?: number | null
  pricing_model?: PricingModel
  rate_per_sqft_private?: number | null
  rate_per_sqft_shared?: number | null
  status?: HangarStatus
  type_config?: HangarTypeConfig
  settings?: Record<string, unknown>
}

/** Form data interface (what the form submits) */
export interface HangarFormData {
  name: string
  area_code: string
  type: HangarType
  exterior_width: number
  exterior_depth: number
  exterior_height: number
  latitude: number
  longitude: number
  altitude?: number | null
  rotation?: number | null
  door_width?: number | null
  door_height?: number | null
  // Building features
  construction_year?: number | null
  fire_suppression?: boolean
  security_system?: boolean
  // Pricing
  monthly_rate_private?: number | null
  monthly_rate_shared?: number | null
  transient_rate_nightly?: number | null
  pricing_model?: PricingModel
  rate_per_sqft_private?: number | null
  rate_per_sqft_shared?: number | null
  status: HangarStatus
  type_config?: HangarTypeConfig
}

/** Table row type (subset for list display) */
export interface HangarTableRow {
  id: string
  name: string
  area_code: string
  type: HangarType
  exterior_width: number
  exterior_depth: number
  exterior_height: number
  total_footprint_sqft: number
  status: HangarStatus
  location_id: string
  created_at: string
  /** Unit count for T-hangars (from units table) */
  unit_count?: number
}

/** Filter options for hangars list */
export interface HangarFilters {
  search?: string
  type?: HangarType | null
  status?: HangarStatus | null
  locationId?: string
}
